import {Localize} from "@dp-instant/i18n";
import {AdResponse} from "@dp-instant/interfaces";
import {useInjection} from "@dp-instant/ui";
import {GameModel} from "@game/coregame/model/GameModel";
import {
    ISelectableBuilding,
    ISelectableColor,
    ISelectableFighter,
    PlayerColor,
    SelectableColorCss
} from "@game/coregame/settings";
import {playUIClickSound, showRewardOrPay} from "@game/helpers";
import {TypesGame} from "@game/TypesGame";
import {ShopItem, ShopItemProps} from "@game/ui/components/Shop/ShopItem/ShopItem";
import {ShopType, UIConstants} from "@game/ui/constants";
import {SVG} from "@game/ui/images";
import cn from "classnames";
import {useEffect, useState} from "preact/hooks";
import {SkinManager} from "@game/coregame/managers/SkinManager";
import {ShopTabHeader, ShopTabHeaderProps} from "@game/ui/components/Shop/ShopTabHeader";
import {Playdeck} from "@game/coregame/utils/Playdeck";
import {apiClient} from "@game/api/ApiClient";
import {CatalogItemDTO} from "../../../../packages/shared/src/index";
import {PaymentMethodPopup} from "@game/ui/popups/PaymentMethodPopup";
import {TransactionHistoryPopup} from "@game/ui/popups/TransactionHistoryPopup";
import {RewardedProgressBar} from "./RewardedProgressBar";
import "./ShopItem/ShopItem.less";

type ShopItemData = {
    id?: number | string;
    type: ShopType;
    content?: ShopItemProps;
};

type ShopTabData = {
    type: ShopType;
    isActive: boolean;
    header: ShopTabHeaderProps;
    items: ShopItemData[];
};

let lastOpenedTab: ShopType = UIConstants.shop.defaultTabType;

export const setShopTab = (tab: ShopType) => {
    lastOpenedTab = tab;
};

const getTabShopI18n = (type: ShopType): string => {
    switch (type) {
        case ShopType.BUILDING:
            return Localize.get("ui-store-building", "BUILDING");
        case ShopType.FIGHTER:
            return Localize.get("ui-store-fighter", "FIGHTER");
        case ShopType.STARS:
            return Localize.get("ui-store-coins", "COINS");
        default:
            return Localize.get("ui-store-color", "COLOR");
    }
};

const isCheatClick = (e: any) => {
    if (process.env.NODE_ENV === "development") return e?.shiftKey;
};

type ShopMenuState = {
    shopItems: ShopTabData[];
    adViewed: boolean;
    lastOpenedTab: ShopType;
    selectedItemId: number | string | null;
    catalogItems: CatalogItemDTO[];
    topupItems: CatalogItemDTO[];
    buyingSlug: string | null;
    paymentPopup: { slug: string; price: number; title: string; starsAmount?: number; tributePriceUsd?: number } | null;
    showTransactions: boolean;
    starsBalance: number;
};

export const ShopMenu = () => {
    const model = useInjection<GameModel>(TypesGame.model);
    const skinManager = useInjection<SkinManager>(TypesGame.skinManager);
    const initialState: ShopMenuState = {
        shopItems: [],
        adViewed: false,
        lastOpenedTab,
        selectedItemId: null,
        catalogItems: [],
        topupItems: [],
        buyingSlug: null,
        paymentPopup: null,
        showTransactions: false,
        starsBalance: apiClient.hardBalance
    };

    const [state, setState] = useState(() => initialState);
    const {shopItems, adViewed} = state;

    const viewAd = async (
        onSuccess: () => void,
        item?: ISelectableFighter | ISelectableBuilding
    ) => {
        setState(state => ({...state, adViewed: false}));

        const showRewardresult = model.rewardedAdsAllowed ? await showRewardOrPay(model.cookie.getVip, "unlock_skin") : buyForCoins(item);

        //await WaitAction.sec(10);
        if (showRewardresult) {
            onSuccess();
            setState(state => ({...state, adViewed: true}));
        }
    };

    /**
     * In this case it returns AdResponse for compatibility
     */
    const buyForCoins = (item?: ISelectableFighter | ISelectableBuilding): AdResponse => {
        if (item && item.coinsPrice) {
            const price = item.coinsPrice;
            if (model.cookie.isEnoughCoins(price)) {
                model.cookie.spendCoins(price);
                return AdResponse.PLAYED;
            }
        }
        return AdResponse.NOT_SUPPORTED;
    };

    const selectItem = (
        item: ISelectableBuilding | ISelectableFighter | ISelectableColor,
        onSelect: () => void
    ) => {
        if (!item.selected) {
            playUIClickSound();
            onSelect();
            setState(state => ({...state, selectedItemId: item.id}));
        }
    };

    const selectFighter = (item: ISelectableBuilding) => (e: any) => {
        if (item.stored) {
            selectItem(item, () => (model.cookie.selected_fighter_id = item.id));
        } else {
            if (isCheatClick(e)) {
                model.cookie.addUserFighter(item.id);
                // setState({...state, adViewed: true});
                return;
            }

            viewAd(() => model.cookie.addUserFighter(item.id), item);
        }
    };

    const selectBuilding = (item: ISelectableFighter) => (e: any) => {
        if (item.stored) {
            selectItem(item, () => (model.cookie.selected_building_id = item.id));
        } else {
            if (isCheatClick(e)) {
                model.cookie.addUserBuilding(item.id);
                // setState({...state, adViewed: true});
                return;
            }
            viewAd(() => model.cookie.addUserBuilding(item.id), item);
        }
    };

    const selectColor = (item: ISelectableColor) => () =>
        item.stored && selectItem(item, () => (model.cookie.selected_color_set_id = item.id));

    const getShopItemsByTabType = (type: ShopType): ShopItemData[] => {
        let selectedColorSet: PlayerColor | undefined = model.cookie.selectedColorSet?.data;
        let shopItemColorClassName: string =
            SelectableColorCss.find(item => item.id === model.cookie.selectedColorSet?.id)
                ?.className || "";

        switch (type) {
            case ShopType.BUILDING:
                return skinManager.availableBuildings.map(item => ({
                    id: item.id,
                    type,
                    content: {
                        selectedColorSet,
                        textureUrl: item.ui_textureUrl,
                        isSelected: item.selected,
                        stored: item.stored,
                        colorClassName: shopItemColorClassName,
                        onClick: selectBuilding(item),
                        forCoins: item.coinsPrice
                    }
                }));

            case ShopType.FIGHTER:
                return skinManager.availableFighters.map(item => ({
                    id: item.id,
                    type,
                    content: {
                        selectedColorSet,
                        textureUrl: item.ui_textureUrl,
                        isSelected: item.selected,
                        stored: item.stored,
                        colorClassName: shopItemColorClassName,
                        onClick: selectFighter(item),
                        forCoins: item.coinsPrice
                    }
                }));

            case ShopType.COLOR:
                return model.cookie.availableColors.map(item => ({
                    id: item.id,
                    type,
                    content: {
                        isSelected: item.selected,
                        stored: item.stored,
                        colorData: item.data,
                        onClick: selectColor(item)
                    }
                }));
        }
        return [];
    };

    const getAllShopItems = (): ShopTabData[] => {
        const {tabTypes} = UIConstants.shop;
        return tabTypes.map(tabType => {
            const isActive = tabType === lastOpenedTab;
            const title = getTabShopI18n(tabType);
            const items = getShopItemsByTabType(tabType);
            const showNotification = items.filter(item => !item.content?.stored).length > 0;
            return {
                type: tabType,
                isActive,
                items,
                header: {
                    className: isActive ? "shop-menu__tab-header_active" : "",
                    showNotification,
                    title,
                    onClick: () => {
                        playUIClickSound();
                        lastOpenedTab = tabType;
                        setState(state => ({...state, lastOpenedTab: tabType}));
                    }
                }
            };
        });
    };

    // Load catalog for Stars tab (TG only)
    useEffect(() => {
        if (__SOCIAL__ === "tg") {
            apiClient.getCatalog().then(items => {
                const coinItems = items.filter(i => i.active && i.type === "consumable" && (i.payload as any)?.coins);
                const topupItems = items.filter(i => i.active && i.type === "stars_topup");
                setState(s => ({...s, catalogItems: coinItems, topupItems}));
            }).catch(() => {});
        }
    }, []);

    // Reactive balance update
    useEffect(() => {
        const handler = (e: Event) => {
            const balance = (e as CustomEvent).detail as number;
            setState(s => ({...s, starsBalance: balance}));
        };
        window.addEventListener("stars-balance-updated", handler);
        return () => window.removeEventListener("stars-balance-updated", handler);
    }, []);

    const openPaymentPopup = (slug: string, price: number, title: string, starsAmount?: number, tributePriceUsd?: number) => {
        playUIClickSound();
        setState(s => ({...s, paymentPopup: {slug, price, title, starsAmount, tributePriceUsd}}));
    };

    const handlePaymentResult = (confirmed?: boolean) => {
        if (confirmed) {
            const popup = state.paymentPopup;
            if (popup) {
                const item = state.catalogItems.find(i => i.slug === popup.slug);
                if (item && (item.payload as any)?.coins) {
                    model.cookie.coins += (item.payload as any).coins;
                }
            }
        }
        setState(s => ({...s, paymentPopup: null}));
    };

    useEffect(() => {
        const shopItems = getAllShopItems();
        setState(state => ({...state, shopItems}));
    }, [adViewed, state.lastOpenedTab, state.selectedItemId, state.shopItems]);

    if (state.paymentPopup) {
        return (
            <PaymentMethodPopup
                slug={state.paymentPopup.slug}
                price={state.paymentPopup.price}
                title={state.paymentPopup.title}
                starsAmount={state.paymentPopup.starsAmount}
                tributePriceUsd={state.paymentPopup.tributePriceUsd}
                onConfirm={handlePaymentResult}
                onPaid={() => setState(s => ({...s}))}
            />
        );
    }

    return (
        <div className={cn("shop-menu")}>
            <div className={cn("shop-menu__tabs-header")}>
                {shopItems.map(shopTab => (
                    <ShopTabHeader {...shopTab.header} />
                ))}
            </div>
            <div className={cn("shop-menu__tabs-content")}>
                <div className={cn("shop-menu__tab-content")}>
                    {state.lastOpenedTab === ShopType.STARS ? (
                        <div className="shop-stars-sections">
                            {/* Section 1: Buy coins with Stars */}
                            <div className="shop-stars-section">
                                <div className="shop-stars-section__title">{Localize.get("ui-store-coins", "COINS")}</div>
                                <div className="shop-stars-grid">
                                    {state.catalogItems.map(item => {
                                        const coins = (item.payload as any)?.coins;
                                        return (
                                            <div key={item.slug} className={cn("shop-item_stars")}>
                                                <div className="shop-item_stars__coin-icon"><SVG.COINS /></div>
                                                <div className="shop-item_stars__amount">{coins ? `${coins}` : item.title}</div>
                                                <button
                                                    className={cn("btn-green", "shop-item__buy-btn")}
                                                    disabled={state.buyingSlug === item.slug}
                                                    onClick={async () => {
                                                        playUIClickSound();
                                                        setState(s => ({...s, buyingSlug: item.slug}));
                                                        try {
                                                            const {paid} = await apiClient.purchaseFlow(item.slug);
                                                            if (paid && coins) {
                                                                model.cookie.coins += coins;
                                                                (window as any).__syncCoins?.();
                                                                setState(s => ({...s, buyingSlug: null}));
                                                            } else {
                                                                setState(s => ({...s, buyingSlug: null}));
                                                            }
                                                        } catch {
                                                            setState(s => ({...s, buyingSlug: null}));
                                                            const tg = (window as any).Telegram?.WebApp;
                                                            tg?.showAlert?.(Localize.get("ui-payment-error", "Payment error. Please try again."));
                                                        }
                                                    }}
                                                >
                                                    {state.buyingSlug === item.slug ? "..." : `${item.price} ⭐`}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Section 2: Top up Stars balance */}
                            {state.topupItems.length > 0 && (
                                <div className="shop-stars-section">
                                    <div className="shop-stars-section__title-row">
                                        <div className="shop-stars-section__title">{Localize.get("ui-store-topup-stars", "TOP UP")} · {state.starsBalance} ⭐</div>
                                        <button
                                            className="shop-stars-section__tx-btn"
                                            onClick={() => { playUIClickSound(); setState(s => ({...s, showTransactions: true})); }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <polyline points="12 6 12 12 16 14" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="shop-stars-grid">
                                        {state.topupItems.map(item => {
                                            const stars = (item.payload as any)?.stars;
                                            return (
                                                <div key={item.slug} className={cn("shop-item_stars", "shop-item_stars--topup")}>
                                                    <div className="shop-item_stars__star-icon">⭐</div>
                                                    <div className="shop-item_stars__amount">{stars ?? item.title}</div>
                                                    <button
                                                        className={cn("btn-green", "shop-item__buy-btn")}
                                                        onClick={() => openPaymentPopup(item.slug, item.price, `${stars} Stars`, stars, item.tributePriceUsd)}
                                                    >
                                                        {`$${(item.price * 0.015).toFixed(2)}`}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Rewarded Progress Bar — end of Stars section */}
                            {__SOCIAL__ === "tg" && <RewardedProgressBar isPremium={model.cookie.getVip} onBalanceChange={(soft, hard) => {
                                if (soft > 0) model.cookie.coins += soft;
                                if (hard > 0) {
                                    apiClient.hardBalance += hard;
                                    apiClient.emitHardBalanceUpdate();
                                }
                                (window as any).__syncCoins?.();
                            }} />}
                        </div>
                    ) : (
                        shopItems.map(shopTab =>
                            shopTab.isActive
                                ? shopTab.items.map(shopItem => (
                                      <ShopItem
                                          id={shopItem.id}
                                          type={shopItem.type}
                                          {...shopItem.content}
                                      />
                                  ))
                                : null
                        )
                    )}
                </div>
            </div>
            {state.showTransactions && (
                <TransactionHistoryPopup onClose={() => setState(s => ({...s, showTransactions: false}))} />
            )}
        </div>
    );
};
